import React, { FunctionComponent, useEffect } from 'react';
import {
    areProgramMetadataValid,
    formTimeString,
    getLiveDateStringFromPercentageWithinDVRWindow,
    getLiveDateStringFromPercentageWithinProgramWindow,
    getLiveProgressFromPercentageWithinDVRWindow
} from '@24i/player-utils';
// USE /src imports to help next.js process less files to reduce bundle, avoid some problematic files, and speed up build.

import { useTheme } from 'Theme';
import { ProgressTimeContainer } from '../../styled';
import { Time, Container } from './styled';
import { IProgressTime } from '../../../Timeline/types';

const PERCENT = 100;
const HOUR_MS = 60 * 60 * 1000;

export const getProgressTimeString = ({
    progress = 0,
    duration,
    playerInstance,
    programRange = null,
    showHours = true,
    forceLiveTime = false
}) => {
    const numericPercentage = progress / PERCENT;
    const isLive = forceLiveTime ? true : !!playerInstance?.live;
    const { start, end } = playerInstance?.timeshiftRange || {
        start: 0,
        end: 0
    };
    const { programStart, programEnd } = playerInstance?.getMetadata() || {
        programStart: 0,
        programEnd: 0
    };
    const rangeStart = programRange?.start ?? programStart;
    const rangeEnd = programRange?.end ?? programEnd;

    let milliseconds: number;
    if (!isLive) {
        milliseconds = numericPercentage * duration;
    } else if (areProgramMetadataValid(rangeStart, rangeEnd)) {
        milliseconds = getLiveDateStringFromPercentageWithinProgramWindow(
            rangeStart,
            rangeEnd,
            numericPercentage
        );
    } else {
        const target = getLiveProgressFromPercentageWithinDVRWindow(start, end, numericPercentage);
        const timestamp = playerInstance.getUTCTimestamp(target);
        if (timestamp) {
            milliseconds = timestamp.getTime();
        } else {
            milliseconds = getLiveDateStringFromPercentageWithinDVRWindow(
                start,
                end,
                numericPercentage,
                Date.now()
            );
        }
    }

    const finalShowHours = showHours === true || (showHours === 'auto' && milliseconds > HOUR_MS);
    const timeString = formTimeString(milliseconds, finalShowHours, isLive);
    return timeString;
};

export const ProgressTime: FunctionComponent<IProgressTime> = ({
    progress = 0,
    duration,
    isVisible,
    className = '',
    playerInstance,
    programRange,
    showHours = true,
    styles = {
        container: null,
        backgroundContainer: null,
        time: null
    },
    forceLiveTime = false,
    overrideTime
}) => {
    const numericPercentage = progress / PERCENT;
    const isLive = forceLiveTime ? true : !!playerInstance?.live;
    const { start, end } = playerInstance?.timeshiftRange || {
        start: 0,
        end: 0
    };
    const { programStart, programEnd } = playerInstance?.getMetadata() || {
        programStart: 0,
        programEnd: 0
    };
    const rangeStart = programRange?.start ?? programStart;
    const rangeEnd = programRange?.end ?? programEnd;

    let milliseconds: number;
    if (!isLive) {
        milliseconds = numericPercentage * duration;
    } else if (areProgramMetadataValid(rangeStart, rangeEnd)) {
        milliseconds = getLiveDateStringFromPercentageWithinProgramWindow(
            rangeStart,
            rangeEnd,
            numericPercentage
        );
    } else {
        const target = getLiveProgressFromPercentageWithinDVRWindow(start, end, numericPercentage);
        const timestamp = playerInstance.getUTCTimestamp(target);
        if (timestamp) {
            milliseconds = timestamp.getTime();
        } else {
            milliseconds = getLiveDateStringFromPercentageWithinDVRWindow(
                start,
                end,
                numericPercentage,
                Date.now()
            );
        }
    }

    const finalShowHours = showHours === true || (showHours === 'auto' && milliseconds > HOUR_MS);
    const timeString = overrideTime || formTimeString(milliseconds, finalShowHours, isLive);

    const { theme } = useTheme();

    if (!isVisible) return null;

    return (
        <ProgressTimeContainer
            {...{ progress }}
            className={`${className} ProgressTime`}
            style={styles.container}
        >
            <Container
                style={styles.backgroundContainer}
                data-test="ProgressTime-Container"
                className="ProgressTime-Container"
            >
                <Time
                    style={styles.time}
                    data-test="ProgressTime-Time"
                    className="ProgressTime-Time"
                    theme={theme}
                >
                    {timeString}
                </Time>
            </Container>
        </ProgressTimeContainer>
    );
};
